/**
 * Communication Service - API client for announcements, messages,
 * and conversations endpoints
 */
import api from '../utils/api';

const communicationService = {
  // ---- Announcements ----

  getAnnouncements: async () => {
    try {
      return await api.get('/api/communication/announcements');
    } catch (error) {
      console.error('Error fetching announcements:', error);
      throw error;
    }
  },

  createAnnouncement: async (data) => {
    try {
      return await api.post('/api/communication/announcements', data);
    } catch (error) {
      console.error('Error creating announcement:', error);
      throw error;
    }
  },

  // ---- Conversations & messages ----

  getConversations: async (page = 1, limit = 20) => {
    try {
      return await api.get('/api/communication/conversations', { params: { page, limit } });
    } catch (error) {
      console.error('Error fetching conversations:', error);
      throw error;
    }
  },

  getConversationMessages: async (conversationId, page = 1, limit = 50) => {
    try {
      return await api.get(`/api/communication/conversations/with/${conversationId}`, {
        params: { page, limit }
      });
    } catch (error) {
      console.error('Error fetching conversation messages:', error);
      throw error;
    }
  },

  sendMessage: async (payload) => {
    try {
      return await api.post('/api/communication/messages', payload);
    } catch (error) {
      console.error('Error sending message:', error);
      throw error;
    }
  },

  markMessageAsRead: async (messageId) => {
    try {
      return await api.put(`/api/communication/messages/${messageId}/read`);
    } catch (error) {
      console.error('Error marking message as read:', error);
      throw error;
    }
  },

  // ---- Attachments ----

  uploadAttachment: async (messageId, file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      return await api.post(`/api/communication/messages/${messageId}/attachments`, formData);
    } catch (error) {
      console.error('Error uploading attachment:', error);
      throw error;
    }
  },

  getAttachments: async (messageId) => {
    try {
      return await api.get(`/api/communication/messages/${messageId}/attachments`);
    } catch (error) {
      console.error('Error fetching attachments:', error);
      throw error;
    }
  },

  // Raw mode — for file downloads (blob), same pattern as
  // payrollService.js's getSsnitExport/getPayslipPdf. Caller does
  // response.blob() and triggers the browser download.
  downloadAttachment: async (attachmentId) => {
    try {
      return await api.get(`/api/communication/attachments/${attachmentId}/download`, { raw: true });
    } catch (error) {
      console.error('Error downloading attachment:', error);
      throw error;
    }
  },

  // ---- Group conversations ----

  createGroupConversation: async (data) => {
    try {
      return await api.post('/api/communication/group-conversations', data);
    } catch (error) {
      console.error('Error creating group conversation:', error);
      throw error;
    }
  },

  listGroupConversations: async () => {
    try {
      return await api.get('/api/communication/group-conversations');
    } catch (error) {
      console.error('Error fetching group conversations:', error);
      throw error;
    }
  },

  getGroupMessages: async (conversationId) => {
    try {
      return await api.get(`/api/communication/group-conversations/${conversationId}/messages`);
    } catch (error) {
      console.error('Error fetching group messages:', error);
      throw error;
    }
  },

  sendGroupMessage: async (conversationId, content) => {
    try {
      return await api.post(`/api/communication/group-conversations/${conversationId}/messages`, { content });
    } catch (error) {
      console.error('Error sending group message:', error);
      throw error;
    }
  },

  // ---- Contacts ----

  getTeachersByStudent: async (studentId) => {
    try {
      return await api.get(`/api/communication/teachers/by-student/${studentId}`);
    } catch (error) {
      console.error('Error fetching teachers for student:', error);
      throw error;
    }
  },

  getParentsByStudent: async (studentId) => {
    try {
      return await api.get(`/api/communication/parents/by-student/${studentId}`);
    } catch (error) {
      console.error('Error fetching parents for student:', error);
      throw error;
    }
  }
};

export default communicationService;
